import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AdminCrudPage, AdminCrudPageConfig } from '@/components/admin';
import api from '@/services/api';

/**
 * Add-ons/riders (global list -- not scoped to one product; see
 * `ProductFormPage`'s inline Add-Ons editor for the per-product version used
 * on the Products screen, item 10 of the 2026-09-18 batch).
 *
 * Found broken while building that item: this config's columns and form
 * fields were `premium_type` and `amount`, neither of which exists on
 * `ProductAddOn` or on `ProductAddOnCreateUpdateSerializer` -- the real fields
 * are `is_percentage_based` (a boolean, not a "percentage"/"flat" choice) plus
 * `percentage_of_premium` and `fixed_premium`. Every create through this
 * screen would have 400'd on `amount`/`premium_type` not being recognised
 * fields (silently discarded by DRF, not even rejected) while the columns
 * rendered blank for every row -- the same shape of bug as the historical NCB
 * Slabs screen's wrong-field-names issue (see `NcbSlabsPage`'s docstring).
 */
const ADDON_TYPE_OPTIONS = [
  { value: 'cover', label: 'Additional Cover' },
  { value: 'rider', label: 'Rider' },
  { value: 'benefit', label: 'Benefit' },
];

type AddonRow = { id: string; [key: string]: unknown };

const baseConfig: AdminCrudPageConfig<AddonRow> = {
  title: 'Add-ons',
  apiEndpoint: '/products/add-ons/',
  queryKey: 'productAddons',
  // Widths turn on the fixed table layout (G2) `AdminCrudPage` now supports.
  columns: [
    { key: 'name', label: 'Name', width: '20%', sortable: true },
    { key: 'product_name', label: 'Product', width: '20%', render: (v: unknown) => (v as string) || 'Global' },
    { key: 'insurance_type_name', label: 'Insurance Type', width: '16%', render: (v: unknown) => (v as string) || '-' },
    { key: 'addon_type_display', label: 'Type', width: '12%' },
    {
      key: 'is_percentage_based',
      label: 'Pricing',
      width: '16%',
      render: (_v: unknown, row) => {
        const r = row as unknown as {
          is_percentage_based: boolean;
          percentage_of_premium: string | null;
          fixed_premium: string | null;
        };
        return r.is_percentage_based
          ? `${r.percentage_of_premium ?? '-'}% of premium`
          : `Flat ${r.fixed_premium ?? '-'}`;
      },
    },
    { key: 'is_active', label: 'Status', width: '10%', render: (v: unknown) => (v ? 'Active' : 'Inactive') },
  ],
  tableLayout: 'fixed',
  formFields: [
    { name: 'name', label: 'Name', type: 'text', required: true },
    { name: 'code', label: 'Code', type: 'text', required: true },
    { name: 'product', label: 'Product (leave empty for a global add-on)', type: 'select', apiEndpoint: '/products/products/', searchable: true },
    { name: 'insurance_type', label: 'Insurance Type (required when there is no product)', type: 'select', apiEndpoint: '/products/types/', searchable: true },
    { name: 'addon_type', label: 'Type', type: 'select', options: ADDON_TYPE_OPTIONS, required: true },
    { name: 'is_percentage_based', label: 'Percentage of premium (unchecked = flat amount)', type: 'boolean' },
    { name: 'percentage_of_premium', label: 'Percentage of Premium', type: 'number' },
    { name: 'fixed_premium', label: 'Fixed Premium', type: 'number' },
    { name: 'is_mandatory', label: 'Mandatory', type: 'boolean' },
    { name: 'is_active', label: 'Active', type: 'boolean' },
  ],
  hasActivate: true,
};

export function AddonsPage() {
  // Filters (SPEC-328 section 5): by insurance type, and "global only" (no product).
  const { data: types = [] } = useQuery<{ id: string; name: string }[]>({
    queryKey: ['insurance-types-lookup-for-addon-filter'],
    queryFn: async () => {
      const res = await api.get('/products/types/', { params: { page_size: 200 } });
      return res.data.results || res.data;
    },
  });
  const config = useMemo<AdminCrudPageConfig<AddonRow>>(
    () => ({
      ...baseConfig,
      filters: [
        { key: 'insurance_type', label: 'All insurance types', options: types.map((t) => ({ value: t.id, label: t.name })) },
        { key: 'global', label: 'All add-ons', options: [{ value: 'true', label: 'Global only (no product)' }] },
      ],
    }),
    [types],
  );
  return <AdminCrudPage config={config} />;
}
